/* LiveChat.jsx – continuous chat with pre-LLM trigger system */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useConversation } from '@elevenlabs/react';
import { useJesseConversation } from './hooks/useJesseConversation';
import { useLiveChatStatus } from './hooks/useLiveChatStatus';
import { cleanBotMessage, detectActualVacancyText, cleanVacancyText } from './utils/vacancyUtils';
import { generateVacancyPDF, generateChatPDF } from './utils/pdfGenerator';
import { buildEmailBody, openNativeShare, tryMailtoFallback, copyToClipboard } from './utils/shareUtils';
import { notify } from './utils/notifications';
import { AGENT_ID, XI_KEY, PROGRESS_STEPS, JOB_KEYWORDS, STEP_PATTERNS, VACANCY_COMPLETION_PATTERNS } from './config/livechat';
import Header from './components/Header';
import ErrorMessage from './components/ErrorMessage';
import UniversalProgressBar from './components/UniversalProgressBar';
import FormattedChatBubble from './components/FormattedChatBubble';
import TextChat from './components/TextChat';
import VacancyReviewScreen from './components/VacancyReviewScreen';
import UploadModal from './components/UploadModal';
import StatusDisplay from './components/livechat/StatusDisplay';
import IntroContent from './components/livechat/IntroContent';
import TipsModal from './components/livechat/TipsModal';
import HelpModal from './components/livechat/HelpModal';

import './components/Header.css';
import './haiMikeStyles.css';
import './chatTheme.css';

// Feature flag: switch to own WebSocket pipeline (Whisper STT + Azure GPT + Parkiet/ElevenLabs TTS)
// Set REACT_APP_USE_OWN_PIPELINE=true to enable — defaults to ElevenLabs agent
const USE_OWN_PIPELINE = process.env.REACT_APP_USE_OWN_PIPELINE === 'true';

// ══════════════════════════════════════════════════════════════
// TEST_MODE: Zet op true om het VacancyReviewScreen te testen
// met vooraf ingevulde data. Zet op false voor productie.
// ══════════════════════════════════════════════════════════════
const TEST_MODE = false;

const TEST_VACANCY_TEXT = `**Senior Software Developer**

Heb je zin om mee te bouwen aan de toekomst van recruitment? Wij zoeken een Senior Software Developer die net zo enthousiast wordt van slimme technologie als wij. Bij HumanAIze werk je aan Jesse, de digitale recruiter die vacatureteksten schrijft alsof hij er zelf bij zit.

Wat ga je doen
- Je bouwt nieuwe features voor ons AI-platform in React en Node.js
- Je werkt samen met ons data-team aan de integratie van taalmodellen
- Je denkt mee over architectuur en schaalbaarheid
- Je reviewt code van collega's en deelt je kennis

Wie ben jij
- Minimaal 3 jaar ervaring met React en moderne JavaScript
- Ervaring met Node.js of vergelijkbare backend-technologie
- Je bent nieuwsgierig naar AI en de mogelijkheden van grote taalmodellen
- Je communiceert helder en werkt graag in een klein, hecht team

Wat bieden wij
- Een competitief salaris tussen EUR 4.500 en EUR 6.000 bruto per maand
- Flexibel werken: kies zelf je werkplek en werktijden
- 28 vakantiedagen en een persoonlijk ontwikkelbudget
- Een MacBook Pro en alles wat je nodig hebt om lekker te werken
- Lunch op kantoor en regelmatige teamuitjes

Waarom zou je hier willen werken
Wij zijn geen corporate machine. Bij HumanAIze ben je geen radertje maar een mede-bouwer. Je werk heeft direct impact op duizenden recruiters die dagelijks met Jesse werken. En ja, de koffie is ook gewoon goed.`;

export default function LiveChat() {
  const { getAccessTokenSilently } = useAuth0();
  const [messages,setMessages]   = useState(TEST_MODE ? [{role:'user',content:'Test gesprek'},{role:'assistant',content:'Test antwoord'}] : []);
  const [started,setStarted]     = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [muted,setMuted]         = useState(false);
  const [speaking,setSpeaking]   = useState(false);
  const [chatOpen,setChatOpen]   = useState(false);
  const [recording, setRecording] = useState(false);

  // Enhanced status system
  const { currentStatus, showEnhancedStatus, showStatus, hideStatus, showEnhancedStatusMessage, hideEnhancedStatus } = useLiveChatStatus();
  const [isGeneratingVacancy, setIsGeneratingVacancy] = useState(false);
  const [micTemporarilyDisabled, setMicTemporarilyDisabled] = useState(false);

  // Processing state
  const [processingTimer, setProcessingTimer] = useState(null);

  // Audio state tracking
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // NEW: Pre-LLM processing state
  const [pendingTranscript, setPendingTranscript] = useState(null);
  const [isPreProcessing, setIsPreProcessing] = useState(false);
  const [preProcessingHooks, setPreProcessingHooks] = useState([]);


  const [progressStep, setProgressStep] = useState(0);
  const [showTextChat, setShowTextChat] = useState(false);
  const [showVacancyReview, setShowVacancyReview] = useState(TEST_MODE);
  const [finalVacancyText, setFinalVacancyText] = useState(TEST_MODE ? TEST_VACANCY_TEXT : null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedVacancyText, setUploadedVacancyText] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [hasClosedReview, setHasClosedReview] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(true);
  const [isStoppingConversation, setIsStoppingConversation] = useState(false);
  const [hasShownReviewScreen, setHasShownReviewScreen] = useState(false);
  const [reviewScreenCooldown, setReviewScreenCooldown] = useState(false);

  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  const audioRef = useRef(null);
  const bottom = useRef(null);
  const chatContainerRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const connectionMonitorRef = useRef(null);
  const vacancyProcessingRef = useRef(false);
  const sessionActiveRef = useRef(false); // Guards tegen typing dots na stop

  const handleCloseProgressBar = () => {
    setShowProgressBar(false);
  };

  const handleFileUploaded = (text, fileName) => {
    
    setUploadedVacancyText(text);
    setUploadedFileName(fileName);
    
    // Open de text chat direct met de geüploade tekst
    setShowTextChat(true);
    
    // Success notification
    notify.showSuccessToast(
      'Bestand geüpload!', 
      `${fileName} is geladen. Jesse gaat je helpen met verbeteren.`
    );
  };

  // ===== GEBRUIK IN MAIN COMPONENT =====
  // Voeg deze state toe aan je LiveChat component:
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  
  const push = (text, who, type) => setMessages(p => {
    const filtered = p.filter(m => !(m.who === who && m.type === 'typing'));
    const timestamp = Date.now();
    return [...filtered, {
      id: timestamp,
      text, 
      who, 
      type,
      timestamp
      // Geen sessionId meer
    }];
  });
  

  // Auto-scroll: alleen als gebruiker al dicht bij de onderkant is
  useEffect(() => {
    if (!started || messages.length === 0) return;

    const realMessages = messages.filter(m => m.type !== 'typing');
    if (realMessages.length <= prevMessageCountRef.current) {
      prevMessageCountRef.current = realMessages.length;
      return;
    }
    prevMessageCountRef.current = realMessages.length;

    // Check of gebruiker dicht bij de onderkant is (binnen 400px)
    const scrollBottom = window.innerHeight + window.scrollY;
    const pageHeight = document.documentElement.scrollHeight;
    const isNearBottom = (pageHeight - scrollBottom) < 400;

    if (!isNearBottom) return; // Gebruiker leest eerdere berichten — niet storen

    // Scroll naar het laatste bericht, maar 150px boven de progress bar
    requestAnimationFrame(() => {
      if (bottom.current) {
        const rect = bottom.current.getBoundingClientRect();
        const targetY = window.scrollY + rect.top - window.innerHeight + 200; // 200px ruimte voor progress bar
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
      }
    });
  }, [messages, started]);
  
  // Scroll naar top bij mount + set page title
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'HAI Job';
  }, []);
  
  // Voeg toe aan je bestaande useEffect in LiveChat.jsx
  const updateProgressStep = useCallback((jesseResponse = '', userInput = '') => {
    // GEBRUIK REAL-TIME progressStep via setProgressStep callback
    setProgressStep(currentStep => {
      let newStep = currentStep;
      
      // BELANGRIJKE FIX: Check eerst op complete vacancy VOORDAT we andere stappen checken
      if (jesseResponse && jesseResponse.length > 400) {
        // Check of dit een complete vacature is
        const isCompleteVacancy = detectVacancyGeneration(jesseResponse);
        if (isCompleteVacancy) {
          return 5;
        }
      }
      
      // Alleen verdere progress checks als we nog niet bij stap 5 zijn
      if (currentStep < 5) {
        
        // STAP 1: FUNCTIETITEL IDENTIFICATIE (0 → 1)
        if (userInput && userInput.length > 5 && currentStep === 0) {
          const inputLower = userInput.toLowerCase();
          const hasJobKeyword = JOB_KEYWORDS.some(kw => inputLower.includes(kw));
          if (hasJobKeyword) {
            newStep = 1;
          }
        }
        
        // STAP 2: WERKZAAMHEDEN (1 → 2)
        else if (jesseResponse && jesseResponse.length > 20 && currentStep === 1) {
          const text = jesseResponse.toLowerCase();
          if (STEP_PATTERNS.step2.some(p => text.includes(p))) newStep = 2;
        }

        // STAP 3: VOORDELEN SOLLICITANT (2 → 3)
        else if (jesseResponse && jesseResponse.length > 20 && currentStep === 2) {
          const text = jesseResponse.toLowerCase();
          if (STEP_PATTERNS.step3.some(p => text.includes(p))) newStep = 3;
        }

        // STAP 4: FUNCTIE-EISEN (3 → 4)
        else if (jesseResponse && jesseResponse.length > 20 && currentStep === 3) {
          const text = jesseResponse.toLowerCase();
          if (STEP_PATTERNS.step4.some(p => text.includes(p))) newStep = 4;
        }

        // STAP 5: ARBEIDSVOORWAARDEN & VACATURE AFRONDING (4 → 5)
        else if (jesseResponse && jesseResponse.length > 20 && currentStep === 4) {
          const text = jesseResponse.toLowerCase();

          const isVacancyComplete = jesseResponse.length > 400 && (
            text.includes('functieomschrijving') || text.includes('werkzaamheden') ||
            text.includes('vereisten') || text.includes('wij bieden') ||
            text.includes('functie-eisen') || text.includes('arbeidsvoorwaarden') ||
            jesseResponse.split('\n').length > 8
          );

          const hasStep5Pattern = STEP_PATTERNS.step5.some(p => text.includes(p));
          const hasVacancyCompletion = VACANCY_COMPLETION_PATTERNS.some(p => text.includes(p));

          if (hasStep5Pattern || isVacancyComplete || hasVacancyCompletion) {
            newStep = 5;
          }
        }
      }
      
      // Log progress update
      if (newStep > currentStep) {
      } else {
      }

      // FALLBACK: Als we bij stap 4 zijn en een lange response krijgen, check alsnog
      if (currentStep === 4 && jesseResponse && jesseResponse.length > 300) {
        if (detectVacancyGeneration(jesseResponse)) {
          return 5;
        }
      }

      return newStep;
    });
  }, []); // Geen dependencies - gebruikt setProgressStep callback

  // ✅ NIEUWE CALLBACK HANDLER voor TextChat updates
  const handleVacancyUpdate = useCallback((updateData) => {
    
    const { updatedText, originalFileName, source } = updateData;
    
    // Update de finale vacaturetekst
    setFinalVacancyText(updatedText);
    
    // Update progress naar 100% als het nog niet was
    if (progressStep < 5) {
      setProgressStep(5);
    }
    
    // Voeg een system message toe aan de chat
    push(`📝 Vacaturetekst bijgewerkt via ${originalFileName}`, 'system');
    
    // Show success notification
    notify.showSuccessToast(
      'Vacature bijgewerkt!',
      `De verbeterde versie is klaar. Je kunt deze nu downloaden of verder bewerken.`
    );
    
    // Optioneel: Auto-open het review scherm
    setTimeout(() => {
      setShowVacancyReview(true);
      setHasClosedReview(false); // Reset zodat het blijft open
    }, 1500);
    
  }, [progressStep, setProgressStep]);

  // ✅ OPTIONEEL: Ook callback toevoegen voor VacancyReviewScreen updates
  const handleVacancyReviewUpdate = useCallback((updateData) => {
    
    // Similar logic as TextChat but from review screen
    setFinalVacancyText(updateData.updatedText);
    
    notify.showSuccessToast(
      'Vacature aangepast!',
      'De wijzigingen zijn doorgevoerd in je vacaturetekst.'
    );
  }, []);

  // Scroll naar top bij mount (mobiel + desktop)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const shouldPreventAutoOpen = () => {
      return (
        hasShownReviewScreen ||
        reviewScreenCooldown ||
        isStoppingConversation ||
        showVacancyReview ||
        hasClosedReview
      );
    };
    
    
    // ALLEEN als expliciete fallback conditie wordt getriggerd
    if (finalVacancyText && 
        finalVacancyText.length > 300 && 
        progressStep >= 5 && 
        !shouldPreventAutoOpen()) {
      
      setHasShownReviewScreen(true);
      setShowVacancyReview(true);
      
      if (started) {
        stop().catch(console.error);
      }
      
      notify.showVacancyCompleted();
    }
    
  }, [finalVacancyText, progressStep, hasShownReviewScreen, reviewScreenCooldown, isStoppingConversation, showVacancyReview, hasClosedReview, started]);

  useEffect(() => {
    if (started && !window.hasShownReviewOnce) {
      // Reset flag bij start van nieuw gesprek
    }
  }, [started]);

  useEffect(() => {
    // Als we op 80% staan en de laatste bot message lijkt op een vacature
    if (progressStep === 4 && messages.length > 0) {
      const lastBotMessage = messages.filter(m => m.who === 'bot' && m.type !== 'typing').pop();
      
      if (lastBotMessage && lastBotMessage.text && lastBotMessage.text.length > 400) {
        
        // Check na 5 seconden of we nog steeds op 80% staan
        const timer = setTimeout(() => {
          if (progressStep === 4) { // Nog steeds op 80%
            
            if (detectVacancyGeneration(lastBotMessage.text)) {
            }
          }
        }, 5000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [progressStep, messages]);

  const removeAllTypingDots = () => {
    setMessages(prev => prev.filter(m => m.type !== 'typing'));
  };
  
  const displayError = useCallback((errorMessage) => {
    // Gebruik nieuwe notification system in plaats van oude error system
    notify.showErrorToast('Fout opgetreden', errorMessage);
    
    // Behoud oude fallback voor compatibiliteit
    setError(errorMessage);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
    }, 6000);
  }, []);

  const detectVacancyGeneration = (text) => {
    
    // VOORKOM DUBBELE DETECTIE
    if (isStoppingConversation || hasShownReviewScreen || reviewScreenCooldown) {
      return false;
    }
    
    const text_lower = text.toLowerCase();
    
    // BELANGRIJK: Filter uit samenvattingen en confirmaties
    const isSummaryOrConfirmation = (
      text_lower.includes('ik vat het nog even kort samen') ||
      text_lower.includes('klopt dit plaatje zo') ||
      text_lower.includes('als alles klopt') ||
      text_lower.includes('ga ik de tekst voor je in elkaar draaien') ||
      text_lower.includes('dan weet je zeker dat ik alles goed heb begrepen') ||
      text_lower.includes('samenvatting') ||
      text_lower.includes('puzzelstukjes') ||
      text_lower.includes('kort samen') ||
      (text.endsWith('!') && text_lower.includes('klopt'))
    );
    
    if (isSummaryOrConfirmation) {
      return false;
    }
    
    // STAP 1: VERBETERDE intro detectie - meer flexibel
    const explicitVacancyIntros = [
      'hier is de vacaturetekst',
      'tijd om die vacaturetekst te bakken',
      'alle ingrediënten binnen',
      'hier komt ie',
      'op basis van ons gesprek',
      'ik heb een vacaturetekst opgesteld',
      'laat me een vacaturetekst maken',
      'hier is jullie vacaturetekst',
      'vacaturetekst voor jullie',
      'de definitieve vacaturetekst',
      // NIEUWE PATRONEN TOEGEVOEGD:
      'functietitel:', // Direct start met functietitel
      '**functietitel',  // Markdown functietitel
      'ben jij die', // Jesse's typische start
      'wat vervoer je liever', // Jesse's stijl
      'zoek jij een' // Andere Jesse starts
    ];
    
    // Check ook op directe start met functietitel
    const startsWithJobTitle = (
      text.trim().startsWith('**') || // Markdown title
      /^[A-Z][a-z]+[a-z\s]+(maker|specialist|manager|developer|assistent|medewerker)/i.test(text.trim()) ||
      text_lower.startsWith('functie:') ||
      text_lower.startsWith('vacature:')
    );
    
    const hasExplicitIntro = explicitVacancyIntros.some(intro => 
      text_lower.includes(intro)
    ) || startsWithJobTitle;
    
    // STAP 2: Moet substantieel lang zijn (verlaagd van 600 naar 400)
    const isLongEnough = text.length > 400;
    
    // STAP 3: VERBETERDE structuur detectie
    const hasVacancyStructure = (
      // Werkzaamheden sectie
      (text_lower.includes('wat ga je doen') || text_lower.includes('werkzaamheden') || 
      text_lower.includes('wat gaat') || text_lower.includes('als onze nieuwe')) &&
      // Eisen sectie  
      (text_lower.includes('wie ben jij') || text_lower.includes('functie-eisen') || 
      text_lower.includes('vereisten') || text_lower.includes('je hebt')) &&
      // Aanbod sectie
      (text_lower.includes('wat bieden wij') || text_lower.includes('arbeidsvoorwaarden') ||
      text_lower.includes('wij bieden') || text_lower.includes('waarom zou je'))
    );
    
    // STAP 4: VERBETERDE vacaturetaal detectie
    const hasVacancyLanguage = (
      text_lower.includes('ben jij') ||
      text_lower.includes('zoeken we') ||
      text_lower.includes('solliciteren') ||
      text_lower.includes('cv') ||
      text_lower.includes('motivatie') ||
      text_lower.includes('klinkt dit als muziek') ||
      text_lower.includes('stuur dan') ||
      // NIEUWE PATRONEN:
      text_lower.includes('dan hebben wij') ||
      text_lower.includes('kom je terecht') ||
      text_lower.includes('niet wachten om je te ontmoeten') ||
      text_lower.includes('de baan voor jou')
    );
    
    // STAP 5: VERSOEPELDE vraag-check
    const hasBlockingQuestions = text_lower.includes('?') && (
      text_lower.includes('klopt dit') ||
      text_lower.includes('wat vind je hiervan') ||
      text_lower.includes('goed zo') ||
      text_lower.includes('akkoord')
    );
    
    // STAP 6: Paragraaf check (verlaagd naar 2)
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 50);
    const hasSubstantialParagraphs = paragraphs.length >= 2;
    
    
    // VERSOEPELDE CRITERIA: Meer flexibiliteit
    const isRealVacancy = (
      !isSummaryOrConfirmation &&
      hasExplicitIntro &&
      isLongEnough &&
      hasVacancyStructure &&
      hasVacancyLanguage &&
      !hasBlockingQuestions &&
      hasSubstantialParagraphs
    );
    
    if (isRealVacancy) {
      
      // Voorkom dubbele uitvoering
      if (vacancyProcessingRef.current) {
        return true;
      }
      
      vacancyProcessingRef.current = true;
      setProgressStep(5);
      setFinalVacancyText(text);
      
      // Stop gesprek als het nog actief is
      if (started) {
        setTimeout(async () => {
          try {
            await stop();
            
            // Open review screen na stop
            setTimeout(() => {
              if (!showVacancyReview && !hasShownReviewScreen) {
                setHasShownReviewScreen(true);
                setShowVacancyReview(true);
                notify.showVacancyCompleted();
              }
            }, 1000);
            
          } catch (error) {
            console.error('❌ Error in vacancy completion sequence:', error);
          } finally {
            // Reset na 5 seconden
            setTimeout(() => {
              vacancyProcessingRef.current = false;
            }, 5000);
          }
        }, 1500);
      }
      
      return true;
    }

    return false;
    };

  // Reset progress bij nieuwe sessie

  // Ook reset bij stop
  const stop = async () => {
    // Markeer sessie als inactief EERST — voorkomt dat setTimeout callbacks nieuwe typing dots toevoegen
    sessionActiveRef.current = false;

    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsAudioPlaying(false);

      await (convo.endSession?.() ?? convo.stopSession?.());
      removeAllTypingDots();
      stopProcessingSequence();
      hideStatus();
      setStarted(false);
      setMuted(false);
      setRecording(false);
      setIsGeneratingVacancy(false);
      setMicTemporarilyDisabled(false);
      setIsPreProcessing(false);
      setPendingTranscript(null);

      convo.setMicMuted?.(false);
      push('⏹️ Gesprek gestopt', 'system');

      // Vertraagde cleanup: vangt eventuele typing dots die door lopende setTimeout callbacks worden toegevoegd
      setTimeout(() => {
        if (!sessionActiveRef.current) {
          setMessages(prev => prev.filter(m => m.type !== 'typing'));
        }
      }, 4000);

    } catch (err) {
      console.error('Error stopping session:', err);
      displayError(`Fout bij stoppen van gesprek: ${err.message || 'Onbekende fout'}`);

      // Fallback reset
      setStarted(false);
      setMuted(false);
      setRecording(false);
      setIsGeneratingVacancy(false);
      setMicTemporarilyDisabled(false);
      setIsAudioPlaying(false);
      setIsPreProcessing(false);
      setPendingTranscript(null);
      stopProcessingSequence();
      hideStatus();

      // Fallback vertraagde cleanup
      setTimeout(() => {
        if (!sessionActiveRef.current) {
          setMessages(prev => prev.filter(m => m.type !== 'typing'));
        }
      }, 4000);
    }
  };



  const stopProcessingSequence = useCallback(() => {
    if (processingTimer) {
      processingTimer.clear();
      setProcessingTimer(null);
    }
  }, [processingTimer]);

  // NEW: Function to register pre-processing hooks
  const registerPreProcessingHook = useCallback((hookFunction) => {
    setPreProcessingHooks(prev => [...prev, hookFunction]);
  }, []);

  // ✅ 1. EERST customPreProcessing:
  const customPreProcessing = useCallback(async (transcript) => {
    
    try {
      // Basis validatie
      if (transcript.length < 3) {
        throw new Error('Transcript te kort');
      }
      
      // Content filtering
      const forbiddenWords = ['spam', 'test123', 'hack', 'exploit'];
      if (forbiddenWords.some(word => transcript.toLowerCase().includes(word))) {
        throw new Error('Inhoud niet toegestaan');
      }
      
      // Speciale intentie detectie
      const completionKeywords = [
        'klaar', 'afronden', 'stoppen', 'genoeg', 'voltooid', 'klaar zijn',
        'kunnen we afronden', 'is dit genoeg', 'heb je genoeg informatie'
      ];
      
      if (completionKeywords.some(keyword => transcript.toLowerCase().includes(keyword))) {
      }
      
      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      
    } catch (error) {
      console.error('❌ Pre-processing failed:', error);
      throw error;
    }
  }, [progressStep, messages]);

  // ✅ 2. DAN executePreProcessing:
  const executePreProcessing = useCallback(async (transcript) => {
    setIsPreProcessing(true);
    
    try {
      // Execute all registered hooks
      for (const hook of preProcessingHooks) {
        await hook(transcript);
      }
      
      // Add your custom pre-processing logic here
      await customPreProcessing(transcript);
      
      return true; // Continue to LLM
    } catch (error) {
      console.error('❌ Pre-processing failed:', error);
      displayError('Er ging iets mis tijdens de verwerking van je vraag.');
      return false; // Abort LLM processing
    } finally {
      setIsPreProcessing(false);
    }
  }, [preProcessingHooks, customPreProcessing, displayError]);

  // NEW: Register example hooks on component mount
  useEffect(() => {
    // Example hook 1: Analytics tracking
    registerPreProcessingHook(async (transcript) => {
      // Send to analytics service
      // await analyticsService.track('user_input', { transcript });
    });

    // Example hook 2: Content filtering
    registerPreProcessingHook(async (transcript) => {
      const forbiddenWords = ['spam', 'test123']; // Example
      if (forbiddenWords.some(word => transcript.toLowerCase().includes(word))) {
        throw new Error('Inhoud niet toegestaan');
      }
    });

    // Example hook 3: User context enrichment
    registerPreProcessingHook(async (transcript) => {
      // You could store user preferences, conversation history, etc.
      // await userContextService.enrich(transcript);
    });
  }, [registerPreProcessingHook]);

  /*
  useEffect(() => {
    // Als progress 100% is maar review scherm niet open na 10 seconden
    if (progressStep >= 5 && !showVacancyReview && finalVacancyText) {
      const timer = setTimeout(() => {
        console.log('🔧 Fallback: Opening review screen after delay');
        setShowVacancyReview(true);

        // Stop gesprek als het nog actief is
        if (started) {
          stop().catch(console.error);
        }
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [progressStep, showVacancyReview, finalVacancyText, started]);
*/

  /* Own WebSocket pipeline — active when USE_OWN_PIPELINE=true
     Sends { source, message } format, handled by the legacy shape handler in onMessage below */
  const ownConvo = useJesseConversation({
    getAccessToken: getAccessTokenSilently,
    onMessage: (m) => {
      window.lastActivityTimestamp = Date.now();
      if (m.source === 'user' && m.isFinal) {
        setTimeout(() => {
          updateProgressStep('', m.message);
          push(m.message, 'user');
          setRecording(false);
        }, 100);
      } else if (m.source === 'assistant' && !m.isDelta && m.isFinal) {
        hideEnhancedStatus();
        setIsPreProcessing(false);
        const cleanedText = cleanBotMessage(m.message);
        setTimeout(() => {
          updateProgressStep(m.message, '');
          push(cleanedText, 'bot');
        }, 1200);
      }
    },
    onModeChange: ({ mode }) => {
      setSpeaking(mode === 'speaking');
      window.lastActivityTimestamp = Date.now();
      if (hasStarted && started) {
        if (mode === 'speaking') {
          hideEnhancedStatus();
        } else if (mode === 'listening' && !isPreProcessing && !isGeneratingVacancy) {
          showEnhancedStatusMessage('listening');
        }
      }
    },
    onError: (e) => {
      const errorMessage = e.message || e.toString();
      console.error('Own pipeline error:', errorMessage);
      hideStatus();
      setIsAudioPlaying(false);
      setIsPreProcessing(false);
      push('⚠️ Er ging iets mis. Je kunt het gesprek opnieuw starten.', 'system');
    },
  });


  /* 11Labs hook with enhanced error handling and status management */
  const elevenLabsConvo = useConversation({
    apiKey: XI_KEY,
    agentId: AGENT_ID,
    startConversation: true,
    micMuted: muted || micTemporarilyDisabled,
    useWakeLock: true,
    vadOptions: {
      preSpeechPadFrames: 1,
      negativeSpeechThreshold: 0.35,
      redemptionFrames: 2,
    },
    onModeChange: ({mode}) => {
      setSpeaking(mode === 'speaking');
      lastActivityRef.current = Date.now();
      
      if (hasStarted && started) {
        if (mode === 'speaking') {
          // Jesse is speaking - hide any status messages
          hideEnhancedStatus();
        } else if (mode === 'listening') {
          
          // Only show listening if not currently processing
          if (!isPreProcessing && !isGeneratingVacancy) {
            showEnhancedStatusMessage('listening');
          }
        } else {
          // Other modes - show ready state if not processing
          if (!isPreProcessing && !isGeneratingVacancy) {
            showEnhancedStatusMessage('ready');
          }
        }
      }
    },
    
    // NEW: Hook into voice activity detection for immediate processing feedback
    onVoiceActivityChange: (isActive) => {
      
      if (hasStarted && started && !micTemporarilyDisabled) {
        if (isActive) {
          // User started speaking
          setRecording(true);
          // Remove status when user starts speaking
          hideEnhancedStatus();
        } else {
          // User stopped speaking - IMMEDIATELY show processing
          setRecording(false);
          
          // Show ONLY the processing message when user stops speaking
          showEnhancedStatusMessage('processing');
          setIsPreProcessing(true);
        }
      }
    },
    
    onMessage: m => {
      lastActivityRef.current = Date.now();
      
      const addBot = txt => { 
        hideEnhancedStatus();
        setIsPreProcessing(false);

        const cleanedText = cleanBotMessage(txt);
        const isSystemMessage = txt.includes('🎤') || txt.includes('⏹️') || txt.includes('⏸️') || txt.includes('▶️') || txt.includes('⚠️');
        
        if (isSystemMessage) {
          push(cleanedText, 'bot'); 
          return;
        }
        
        setTimeout(() => {
          // ZORG DAT DEZE LIJN ER IS:
          updateProgressStep(txt, ''); // ← DIT MOET ER ZIJN!
          
          if (detectVacancyGeneration(txt)) {
            setIsGeneratingVacancy(true);
            setMicTemporarilyDisabled(true);
            showStatus('generating_vacancy');
            
            setTimeout(() => {
              setIsGeneratingVacancy(false);
              setMicTemporarilyDisabled(false);
              hideStatus();
            }, 3000);
          }
          
          push(cleanedText, 'bot'); 
        }, 1200);          
      };
      
      /* Handle user transcript */
      if (m.user_transcript_event?.user_transcript) {
        const userText = m.user_transcript_event.user_transcript;
        
        
        setPendingTranscript(userText);
        
        executePreProcessing(userText).then((shouldContinue) => {
          if (shouldContinue) {
            
            setTimeout(() => {
              updateProgressStep('', userText);
              
              push(userText, 'user');
              setRecording(false);
              
              setMessages(prev => prev.filter(m => !(m.who === 'user' && m.type === 'typing')));
            }, 100);
          } else {
            hideEnhancedStatus();
            setRecording(false);
            setIsPreProcessing(false);
          }
          
          setPendingTranscript(null);
        });
        
        return;
      }
      
      if (m.agent_response_event?.agent_response) {
        addBot(m.agent_response_event.agent_response);
        return;
      }
      
      /* legacy shapes */
      if (m.message && m.source) {
        if (m.source === 'user') {
          
          setTimeout(() => {
            updateProgressStep('', m.message);
            push(m.message, 'user');
          }, 100);
        } else {
          addBot(m.message);
        }
        setRecording(false);
        return;
      }
      
      if (m.text && m.role) {
        if (m.role === 'user') {
          setTimeout(() => {
            push(m.text, 'user');
          }, 100);
        } else {
          addBot(m.text);
        }
        setRecording(false);
        return;
      }
    },
    
    onAudio: (payload) => {
      lastActivityRef.current = Date.now();
      
      if (micTemporarilyDisabled) {
        return;
      }
      
      setTimeout(() => {
        let src = null;
        
        try {
          if (typeof payload === 'string' && payload.startsWith('data:audio')) {
            src = payload;
          }
          else if (typeof payload === 'string') {
            src = 'data:audio/mpeg;base64,' + payload;
          }
          else if (payload instanceof ArrayBuffer || payload instanceof Uint8Array) {
            const blob = new Blob([payload], { type: 'audio/mpeg' });
            src = URL.createObjectURL(blob);
          }
          else if (payload instanceof Blob) {
            src = URL.createObjectURL(payload);
          }
          
          if (!src) {
            console.warn('No valid audio source created from payload');
            return;
          }
          
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          
          const audio = new Audio(src);
          audioRef.current = audio;
          audio.volume = 0.9;
          
          audio.addEventListener('loadstart', () => {
            setIsAudioPlaying(true);
          });
          
          audio.addEventListener('canplay', () => {
          });
          
          audio.addEventListener('playing', () => {
            setIsAudioPlaying(true);
          });
          
          audio.addEventListener('ended', () => {
            setIsAudioPlaying(false);

            setTimeout(() => {
              if (!sessionActiveRef.current) return; // Sessie is al gestopt
              setMessages(prev => {
                const filtered = prev.filter(m => !(m.who === 'user' && m.type === 'typing'));
                return [...filtered, {id: Date.now(), text: '', who: 'user', type: 'typing'}];
              });
            }, 2000);
          });

          audio.addEventListener('error', (e) => {
            setIsAudioPlaying(false);

            setTimeout(() => {
              if (!sessionActiveRef.current) return; // Sessie is al gestopt
              setMessages(prev => {
                const filtered = prev.filter(m => !(m.who === 'user' && m.type === 'typing'));
                return [...filtered, {id: Date.now(), text: '', who: 'user', type: 'typing'}];
              });
            }, 3000);
          });
          
          audio.addEventListener('pause', () => {
            if (audio.currentTime < audio.duration) {
              setIsAudioPlaying(false);
            }
          });
          
          audio.play().catch(error => {
            setIsAudioPlaying(false);

            setTimeout(() => {
              if (!sessionActiveRef.current) return; // Sessie is al gestopt
              setMessages(prev => {
                const filtered = prev.filter(m => !(m.who === 'user' && m.type === 'typing'));
                return [...filtered, {id: Date.now(), text: '', who: 'user', type: 'typing'}];
              });
            }, 3000);
          });
          
        } catch (error) {
          console.error('Audio processing error:', error);
          setIsAudioPlaying(false);
        }
      }, 1200);
    },
    
    onError: (e) => {
      const errorMessage = e.message || e.toString();
      console.error('ElevenLabs error:', errorMessage);
      
      hideStatus();
      stopProcessingSequence();
      setIsGeneratingVacancy(false);
      setMicTemporarilyDisabled(false);
      setIsAudioPlaying(false);
      setIsPreProcessing(false);
      setPendingTranscript(null);
      
      // Gebruik nieuwe notification system voor specifieke errors
      if (errorMessage.includes('websocket') && errorMessage.includes('network')) {
        notify.showConnectionError();
      } else if (errorMessage.includes('API key')) {
        notify.showErrorToast(
          'Technisch probleem', 
          'Er is een technisch probleem opgetreden. Probeer later opnieuw.'
        );
      } else if (errorMessage.includes('microphone') || errorMessage.includes('media')) {
        notify.showErrorToast(
          'Microfoon probleem', 
          'Geen toegang tot microfoon. Controleer je browser instellingen.'
        );
      } else {
        notify.showErrorToast(
          'Onbekende fout', 
          'Er ging iets mis met Jesse. Probeer het gesprek opnieuw te starten.'
        );
      }
      
      push('⚠️ Er ging iets mis. Je kunt het gesprek opnieuw starten.', 'system');
      
      if (errorMessage.includes('websocket') || errorMessage.includes('network')) {
        setTimeout(() => {
          if (started) {
            stop();
          }
        }, 2000);
      }
    }
  });

  // Select active conversation provider based on feature flag
  const convo = USE_OWN_PIPELINE ? ownConvo : elevenLabsConvo;

  useEffect(() => {
    if (speaking && !isGeneratingVacancy && sessionActiveRef.current) {
      setMessages(prev => {
        const filtered = prev.filter(m => !(m.who === 'bot' && m.type === 'typing'));
        return [...filtered, {id: Date.now(), text: '', who: 'bot', type: 'typing'}];
      });
    } else if (!speaking) {
      setMessages(prev =>
        prev.filter(m => !(m.who === 'bot' && m.type === 'typing'))
      );
    }
  }, [speaking, isGeneratingVacancy]);

  useEffect(() => {
    return () => {
      if (processingTimer) {
        processingTimer.clear();
      }
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsAudioPlaying(false);
    };
  }, [processingTimer]);

  useEffect(() => {
    if (!started) return;
    
    if (connectionMonitorRef.current) {
      clearInterval(connectionMonitorRef.current);
    }
    
    lastActivityRef.current = Date.now();
    
    connectionMonitorRef.current = setInterval(() => {
      const inactiveTime = Date.now() - lastActivityRef.current;
      
      if (inactiveTime > 300000 && started) {
        displayError("Geen activiteit gedetecteerd voor 2 minuten. Er lijkt een verbindingsprobleem te zijn.");
      }
    }, 10000);
    
    return () => {
      if (connectionMonitorRef.current) {
        clearInterval(connectionMonitorRef.current);
      }
    };
  }, [started, displayError]);


  // Nieuwe start functie met confirmation check
  const startWithConfirmation = async () => {
    // Als er al berichten zijn, vraag confirmatie
    if (messages.length > 0) {
      notify.showNewConversationConfirmation(
        // onConfirm - start nieuw gesprek
        async () => {
          await actuallyStartNewConversation();
        },
        // onCancel - doe niets
        () => {
        }
      );
    } else {
      // Geen berichten, start gewoon
      await actuallyStartNewConversation();
    }
  };

  // Functie die daadwerkelijk nieuw gesprek start
  const actuallyStartNewConversation = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({audio: true});
      
      // ✅ COMPLETE RESET - alles opschonen
      
      // Reset alle states
      setMessages([]); // ← Wis alle berichten
      setProgressStep(0); // ← Reset progress
      setChatOpen(false); // ← Reset chat visibility
      setError(null);
      setShowError(false);
      setIsGeneratingVacancy(false);
      setMicTemporarilyDisabled(false);
      setIsAudioPlaying(false);
      setIsPreProcessing(false);
      setPendingTranscript(null);
      setHasClosedReview(false); // ← NIEUWE REGEL: Reset review close status
      setShowVacancyReview(false); // ← Reset review screen
      setFinalVacancyText(''); // ← Reset final vacancy text
      setIsStoppingConversation(false);
      setHasShownReviewScreen(false);
      setShowProgressBar(true);
      stopProcessingSequence();
      hideStatus();
      
      lastActivityRef.current = Date.now();
      
      await convo.startSession();
      
      sessionActiveRef.current = true;
      setStarted(true);
      setChatOpen(true);
      setHasStarted(true);
      setRecording(true);
      
      // Eenvoudig startbericht
      push('🎤 Gesprek gestart — praat maar!', 'system');
      
      // Success notification
      notify.showConversationStarted();
      
      
    } catch (err) {
      console.error('Error starting session:', err);
      notify.showErrorToast(
        'Kan gesprek niet starten', 
        `${err.message || 'Onbekende fout'} - Controleer je microfoon.`
      );
    }
  };

  // OPTIONEEL: Functie om chat handmatig te wissen (voor als je dat wel wilt)
  const clearChatHistory = () => {
    notify.showClearChatConfirmation(() => {
      setMessages([]);
      setProgressStep(0);
      setChatOpen(false);
      setStarted(false); // Terug naar welkomstscherm
      setHasStarted(false); // Ook deze resetten voor intro screen

      // Scroll naar top na wissen (alleen op desktop, niet mobiel)
      if (window.innerWidth > 768) {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    });
  };

  /* ---------- PDF helpers ---------- */


  const pdfChatWithNotification = () => {
    try {
      generateChatPDF(messages);
      notify.showSuccessToast(
        'PDF gegenereerd!',
        'Chat en vacaturetekst zijn succesvol gedownload als PDF.'
      );
    } catch (error) {
      console.error('PDF generation error:', error);
      notify.showErrorToast(
        'PDF fout',
        'Er ging iets mis bij het genereren van de PDF.'
      );
    }
  };

  const pdfVacatureWithNotification = () => {
    try {
      const success = generateVacancyPDF(messages);
      if (!success) {
        notify.showInsufficientInfoNotification();
        return;
      }
      notify.showSuccessToast(
        'Professionele PDF klaar!',
        'De vacaturetekst is succesvol gedownload als professionele PDF.'
      );
    } catch (error) {
      console.error('PDF generation error:', error);
      notify.showErrorToast(
        'PDF fout',
        'Er ging iets mis bij het genereren van de PDF.'
      );
    }
  };

  // EMAIL VACATURE (MET SLIMME DETECTIE)
  const emailVacature = async () => {
    const rawVacature = detectActualVacancyText(messages);
    const cleanedVacature = cleanVacancyText(rawVacature);

    if (!cleanedVacature || cleanedVacature.length < 100) {
      notify.showInsufficientInfoNotification();
      return;
    }

    const { subject, body } = buildEmailBody(cleanedVacature);

    // Try native share → mailto → clipboard fallback chain
    const shared = await openNativeShare(subject, body);
    if (shared) {
      notify.showSuccessToast('Delen gelukt!', 'De vacaturetekst is gedeeld via je apparaat.');
      return;
    }

    const mailed = tryMailtoFallback(subject, body);
    if (!mailed) {
      const fullEmailText = `ONDERWERP: ${subject}\n\n${body}`;
      const copied = await copyToClipboard(fullEmailText);
      if (copied) {
        notify.showNotification({
          type: 'info',
          icon: 'content_copy',
          title: 'Email app kon niet worden geopend',
          message: 'De volledige emailtekst is gekopieerd naar je klembord. Plak deze in je email programma.',
          actions: [{ text: 'Begrepen', type: 'primary', onClick: () => {} }]
        });
      } else {
        notify.showNotification({
          type: 'warning',
          icon: 'email',
          title: 'Email handmatig verzenden',
          message: 'Open je email programma handmatig en kopieer de tekst uit de chat.',
          actions: [
            { text: 'Chat openen', type: 'primary', onClick: () => {
              const chatContainer = document.querySelector('.chat-container');
              if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
            }},
            { text: 'Sluiten', type: 'secondary', onClick: () => {} }
          ]
        });
      }
    }
  };

  const copyVacatureToClipboard = async () => {
    const rawVacature = detectActualVacancyText(messages);
    const cleanedVacature = cleanVacancyText(rawVacature);

    if (!cleanedVacature || cleanedVacature.length < 100) {
      notify.showInsufficientInfoNotification();
      return;
    }

    const copied = await copyToClipboard(cleanedVacature);
    if (copied) {
      notify.showSuccessToast('Vacaturetekst gekopieerd!', 'De tekst staat nu in je klembord en kan worden geplakt.');
    } else {
      notify.showErrorToast('Kopiëren mislukt', 'Kon tekst niet kopiëren. Selecteer handmatig uit de chat.');
    }
  };

  const lastIndex = messages.length - 1;

  /* ---------- render ---------- */
  return (
    <div className="container">
      {/* Enhanced Status Display with pre-processing support */}
      <StatusDisplay 
        currentState={isPreProcessing ? 'pre_processing' : currentStatus} 
        isVisible={showEnhancedStatus && started}
      />
      

      {/* Error Message */}
      {showError && error && (
        <ErrorMessage 
          message={error} 
          onClose={() => setShowError(false)} 
        />
      )}

      {/* Progress bar - universeel voor alle schermen */}
      {hasStarted && showProgressBar && (
        <UniversalProgressBar 
          currentStep={progressStep} 
          steps={PROGRESS_STEPS}
          isVisible={true}
          conversationActive={started}
          onClose={handleCloseProgressBar}
        />
      )}

      {/* Header */}
      <Header />

      {/* Chat */}
      <div
        ref={chatContainerRef}
        className={`
          chat
          ${chatOpen ? 'open' : 'collapsed'}
          chat-container
          ${hasStarted ? 'visible' : ''}
        `}
      >
        {messages.map((m, i) => {
          // Detecteer of het een vacaturetekst is
          const isVacancyText = m.who === 'bot' && m.text && (
            m.text.length > 400 && (
              m.text.toLowerCase().includes('functieomschrijving') ||
              m.text.toLowerCase().includes('werkzaamheden') ||
              m.text.toLowerCase().includes('vereisten') ||
              m.text.toLowerCase().includes('wij bieden')
            )
          );
          
          return (
            <FormattedChatBubble
              key={m.id}
              who={m.who}
              type={m.type + (isVacancyText ? ' vacancy-text' : '')}
              className={
                m.type === 'typing' || m.type === 'listening' ||
                (i === lastIndex && (speaking || recording))
                  ? 'speaking'
                  : ''
              }
            >
              {m.type === 'typing' ? (
                <div className="typingDots">
                  <span /><span /><span />
                </div>
              ) : m.type === 'listening' ? (
                <em>{m.text}</em>
              ) : (
                m.text
              )}
            </FormattedChatBubble>
          );
        })}
        <div ref={bottom} />
      </div>

      {/* Download buttons - VERBORGEN per gebruikerswens */}
      {false && !started && messages.length > 0 && (
        <div className="download-buttons" style={{ display: 'none' }}>
          {/* Alle download knoppen zijn verborgen */}
        </div>
      )}

      {/* FLOATING BUTTONS - rechts onderin */}
      {(() => {
        // Toon knoppen wanneer er berichten zijn en gesprek niet actief is
        const shouldShowButtons = messages.length > 0 && !started;

        if (shouldShowButtons) {
          return (
            <>
              {/* Groene preview knop */}
              <button
                className="floating-review-btn"
                onClick={() => {

                  // Zorg dat we finale tekst hebben
                  if (!finalVacancyText) {
                    const detected = detectActualVacancyText(messages);
                    const cleaned = cleanVacancyText(detected);
                    if (cleaned && cleaned.length > 50) {
                      setFinalVacancyText(cleaned);
                    } else {
                      // Te weinig tekst — toon review screen met empty state
                      setFinalVacancyText(null);
                    }
                  }

                  setShowVacancyReview(true);
                  setHasClosedReview(false);
                }}
                title="Bekijk vacaturetekst"
              >
                <span className="material-icons">preview</span>
              </button>

              {/* Rode wis chat knop */}
              <button
                className="floating-delete-btn"
                onClick={clearChatHistory}
                title="Wis chat geschiedenis"
              >
                <span className="material-icons">delete</span>
              </button>
            </>
          );
        }
        return null;
      })()}

      {/* Text Chat Component */}
      <TextChat 
        isVisible={showTextChat}
        onClose={() => {
          setShowTextChat(false);
          // Reset upload data wanneer chat wordt gesloten
          setUploadedVacancyText('');
          setUploadedFileName('');
        }}
        initialText={uploadedVacancyText}
        fileName={uploadedFileName}
        onVacancyUpdated={handleVacancyUpdate}
      />

      {/* Originele mic button */}
      {!started && !hasStarted && (
        <button
          className="mic-btn initial"
          onClick={() => { startWithConfirmation(); setHasStarted(true); }}
        >
          <span className="material-icons">mic</span>
        </button>
      )}

      {/* Intro content onder de mic button */}
      {!started && !hasStarted && (
        <>
          <IntroContent 
            onShowTips={() => setShowTipsModal(true)}
            onShowHelp={() => setShowHelpModal(true)}
            onShowUpload={() => setShowUploadModal(true)}
          />
          
          <TipsModal 
            isVisible={showTipsModal}
            onClose={() => setShowTipsModal(false)}
          />
          
          <HelpModal 
            isVisible={showHelpModal}
            onClose={() => setShowHelpModal(false)}
          />
        </>
      )}

      {/* Restart button */}
      {!started && hasStarted && (
        <button
          className="mic-btn restart"
          onClick={startWithConfirmation}
        >
          <span className="material-icons">mic</span>
        </button>
      )}

      {/* Stop button tijdens gesprek */}
      {started && (
        <div className="fab-container" style={{ 
          position: 'absolute',
          top: '240px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          opacity: started ? 1 : 0,
          pointerEvents: started ? 'auto' : 'none',
          transition: 'opacity 0.3s ease 0.2s',
          zIndex: 1000
        }}>
          <button 
            className="fab-btn stop" 
            onClick={stop}
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: '#f44336',
              color: '#fff',
              border: 'none',
              boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'transform 0.1s ease, box-shadow 0.3s ease',
              fontSize: '1.5rem'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            <span className="material-icons" style={{ fontSize: '2.5rem' }}>stop</span>
          </button>
        </div>
      )}
      
      {/* Status indicator when mic is disabled */}
      {(isGeneratingVacancy || micTemporarilyDisabled) && started && (
        <div className="status-indicator">
          <div className="status-text">
            Jesse is bezig met de vacaturetekst opstellen...
          </div>
        </div>
      )}

      {/* Vacancy Review Screen */}
      <VacancyReviewScreen
        vacancyText={finalVacancyText}
        isVisible={showVacancyReview}
        onClose={() => {
          setShowVacancyReview(false);
          setHasClosedReview(true);
          // RESET flags zodat het niet automatisch hereopent
          setHasShownReviewScreen(false);
          setReviewScreenCooldown(false);
        }}
        onChatOpen={() => {
          // Wordt gebruikt als er een aparte chat functie gewenst is
        }}
        onVacancyUpdated={handleVacancyReviewUpdate}
      />

      {/* Upload Modal */}
      <UploadModal
        isVisible={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onFileUploaded={handleFileUploaded}
      />
    </div>
  );
}